import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';

import api from '../../services/api';
import toastError from '../../toasts/toastError';
import { socketConnection } from '../../services/socket';
import moment from 'moment';

const useAuth = () => {
  const navigate = useNavigate();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const socketRef = useRef(null);

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
        setIsAuth(true);
      }
      return config;
    },
    (error) => {
      Promise.reject(error);
    },
  );

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      if (error?.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;

        const { data } = await api.post('/auth/refresh_token');
        if (data) {
          localStorage.setItem('token', data.token);
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
        }
        return api(originalRequest);
      }
      if (error?.response?.status === 401) {
        localStorage.removeItem('token');
        api.defaults.headers.Authorization = undefined;
        setIsAuth(false);
      }
      return Promise.reject(error);
    },
  );

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');

      if (token) {
        try {
          const { data } = await api.post('/auth/refresh_token');
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setIsAuth(true);
          setUser(data.user);
        } catch (err) {
          toastError(err);
        }
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (user && user.id && user.companyId) {
      if (
        socketRef.current &&
        socketRef.current.isConnected &&
        socketRef.current.isConnected()
      ) {
        return;
      }

      const newSocket = socketConnection({ user });
      socketRef.current = newSocket;
    }

    return () => {
      if (socketRef.current && socketRef.current.disconnect) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [user]);

  useEffect(() => {
    if (!socketRef.current || !user?.id || !user?.companyId) {
      return;
    }

    const currentSocket = socketRef.current;
    const eventName = `company-${user.companyId}-user`;

    const handleUserUpdate = (data) => {
      if (data.action === 'update' && data.user && data.user.id === user.id) {
        setUser(data.user);
      }
    };

    if (currentSocket && typeof currentSocket.on === 'function') {
      currentSocket.on(eventName, handleUserUpdate);
    } else {
      console.error(
        'Socket não tem método on ou não está pronto',
        currentSocket,
      );
    }

    return () => {
      if (currentSocket && typeof currentSocket.off === 'function') {
        currentSocket.off(eventName, handleUserUpdate);
        console.log(`Listener removido para: ${eventName}`);
      }
    };
  }, [user?.id, user?.companyId]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post('/auth/login', userData);

      localStorage.setItem('profileImage', data.user.profileImage);

      moment.locale('pt-br');
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = '2999-12-31T00:00:00.000Z';
      } else {
        dueDate = data.user.company.dueDate;
      }
      const vencimento = moment(dueDate).format('DD/MM/yyyy');

      var diff = moment(dueDate).diff(moment(moment()).format());
      var before = moment(moment().format()).isBefore(dueDate);
      var dias = moment.duration(diff).asDays();

      if (before === true) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('companyDueDate', vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user);
        setIsAuth(true);
        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? 'dia' : 'dias'
            } `,
          );
        }

        navigate('/tickets');
        setLoading(false);
      } else {
        localStorage.setItem('token', data.token);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user);
        setIsAuth(true);
        toastError(
          `Opss! Sua assinatura venceu ${vencimento}. Entre em contato com o Suporte para mais informações! `,
        );
        navigate('/financeiro');
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      await api.delete('/auth/logout');
      setIsAuth(false);
      setUser({});
      localStorage.removeItem('token');
      localStorage.removeItem('cshow');
      api.defaults.headers.Authorization = undefined;

      if (socketRef.current && socketRef.current.disconnect) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      setLoading(false);
      navigate('/login');
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get('/auth/me');
      return data;
    } catch (err) {
      console.error(err.message);
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket: socketRef.current,
  };
};

export default useAuth;
